import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { debounceTime, map, pluck, shareReplay, tap } from 'rxjs/operators';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  MyHttpClientFactory,
  ResponseCasingEnum,
  WindowService,
  environment,
} from '@ups/xplat/core';
import { parseData } from '@ups/xplat/utils';
import {
  IGridGroup,
  IInventoryCountDetail,
  IInventoryEquipmentOption,
  IInventoryEquipmentType,
  IInventoryEquipmentTypeOption,
  IInventoryTemplate,
  IInventoryTemplateDetail,
  IInventoryTemplateItemGroup,
  InventoryCountStatusDto,
  ItemDTO,
  ItemListDTO,
  Manufacturer,
  UnitOfMeasureDTO,
  VendorDto,
} from '../models';
import { buildGridDetailGroups } from '../utils/inventory-grid-detail-groups.utils';

const BULK_EDIT_COUNT_SORT_ORDER = `api/inventoryCount/countDetail/sort`;
const BULK_EDIT_TEMPLATE_SORT_ORDER = `api/inventoryTemplate/templateDetail/sort`;
const CREATE_TEMPLATE = `api/inventoryTemplate`;
const GET_EQUIPMENT = `/api/Equipment`;
const GET_EQUIPMENT_TYPES = `/api/equipment/types`;
const GET_ITEM_GROUPS = `/api/item/itemGroup`;
const GET_ITEMS = `/api/Item`;
const GET_ITEMS_LIST = `/api/Item/list`;
const GET_MANUFACTURERS = `/api/item/manufacturers`;
const GET_TETHERINGS = `/api/item/tetherings`;
const GET_UNITOFMEASURES = `/api/Item/unitofmeasure`;
const GET_VENDORS = `/api/item/vendors`;
const INVENTORY_TEMPLATES_ENDPOINT = `/api/inventoryTemplate/odata`;

const COPY_TEMPLATE = (sourceTemplateId) =>
  `api/inventoryTemplate/copy/${sourceTemplateId}`;
const CREATE_TEMPLATE_DETAIL = (itemId) =>
  `api/inventoryTemplate/templateDetail/${itemId}`;
const SORT_TEMPLATE_GROUPS = (inventoryTemplateId) =>
  `api/inventoryTemplate/${inventoryTemplateId}/itemGroupSort`;
const SORT_COUNT_GROUPS = (inventoryCountId) =>
  `api/inventoryCount/${inventoryCountId}/itemGroupSort`;
const INVENTORY_TEMPLATE_BY_EQUIPMENT_ID = (equipmentId) =>
  `api/inventoryTemplate/byEquipmentId/${equipmentId}`;
const MISSING_GROUPS_DETAILS = (templateId, itemGroupId) =>
  `${TEMPLATE_ENDPOINT(templateId)}/itemGroup/${itemGroupId}/missing`;
const REMOVE_TEMPLATE_DETAIL = (templateId, templateDetailId) =>
  `${TEMPLATE_ENDPOINT(templateId)}/templateDetail/${templateDetailId}`;
const TEMPLATE_DETAIL_ENDPOINT = (templateId) =>
  `${TEMPLATE_ENDPOINT(templateId)}/templateDetail`;
const TEMPLATE_ENDPOINT = (templateId) =>
  `/api/inventoryTemplate/${templateId}`;
const TEMPLATE_EQUIPMENTS_ENDPOINT = (templateId) =>
  `${TEMPLATE_ENDPOINT(templateId)}/relatedAssets`;
const PATCH_ITEM_GROUP_ENDPOINT = (itemGroupId) =>
  `${GET_ITEM_GROUPS}/${itemGroupId}`;
const PATCH_ITEM_ENDPOINT = (itemId) => `${GET_ITEMS}/${itemId}`;

@Injectable({
  providedIn: 'root',
})
export class InventoryService {
  win = inject(WindowService);
  public itemGroups: IInventoryTemplateItemGroup[];
  public templateEquipmentOptions: IInventoryEquipmentOption[];
  public templateEquipmentOptions$: Observable<IInventoryEquipmentOption[]>;
  protected httpSparta: HttpClient;
  protected httpToolTracker: HttpClient;
  protected httpViewpoint: HttpClient;
  protected toolTrackerBaseUrl: string;

  constructor(httpClientFactory: MyHttpClientFactory) {
    this.toolTrackerBaseUrl = environment.urls.toolTrackerAPI;
    this.httpViewpoint = httpClientFactory.createHttpClient(
      environment.urls.viewpointAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
    this.httpSparta = httpClientFactory.createHttpClient(
      environment.urls.spartaAPI,
      true,
      ResponseCasingEnum.CamelCase
    );
    this.httpToolTracker = httpClientFactory.createHttpClient(
      this.toolTrackerBaseUrl,
      true,
      ResponseCasingEnum.CamelCase
    );
  }

  getTemplate(templateId: string): Observable<IInventoryTemplate> {
    const url = TEMPLATE_ENDPOINT(templateId);

    return this.httpToolTracker.get<IInventoryTemplate>(url);
  }

  getExistingTemplates(): Observable<IInventoryTemplate[]> {
    const url = INVENTORY_TEMPLATES_ENDPOINT;

    return <Observable<IInventoryTemplate[]>>this.httpToolTracker.get(url);
  }

  getExistingTemplatesByEquipmentId(
    equipmentId: number
  ): Observable<IInventoryTemplate[]> {
    const url = INVENTORY_TEMPLATE_BY_EQUIPMENT_ID(equipmentId);

    return <Observable<IInventoryTemplate[]>>(
      this.httpToolTracker.get<IInventoryTemplate[]>(url)
    );
  }

  updateTemplate(
    inventoryTemplate: IInventoryTemplate
  ): Observable<IInventoryTemplate> {
    const url = TEMPLATE_ENDPOINT(inventoryTemplate.inventoryTemplateId);

    return this.httpToolTracker
      .put<IInventoryTemplate>(url, inventoryTemplate)
      .pipe(this.successNotification);
  }

  getEquipmentTypes(): Observable<IInventoryEquipmentType[]> {
    const url = GET_EQUIPMENT_TYPES;

    return <Observable<IInventoryEquipmentType[]>>(
      this.httpSparta.get(url).pipe(pluck('value'))
    );
  }

  getRawEquipmentOptions(): Observable<IInventoryEquipmentOption[]> {
    const url = GET_EQUIPMENT;
    return this.httpToolTracker.get<IInventoryEquipmentOption[]>(url);
  }

  getEquipmentOptions(
    query?: string,
    currentValues?: IInventoryEquipmentOption[]
  ): Observable<IInventoryEquipmentOption[]> {
    const url = GET_EQUIPMENT;
    if (!this.templateEquipmentOptions$) {
      this.templateEquipmentOptions$ = this.httpToolTracker
        .get<IInventoryEquipmentOption[]>(url)
        .pipe(
          map((templateEquipmentOptions) =>
            templateEquipmentOptions.map((templateEquipmentOption) => ({
              ...templateEquipmentOption,
              equipmentKeyId: templateEquipmentOption.id,
            }))
          ),
          tap(
            (templateEquipmentOptions) =>
              (this.templateEquipmentOptions = templateEquipmentOptions)
          ),
          shareReplay(1)
        );
    }

    return this.templateEquipmentOptions$.pipe(
      debounceTime(500),
      map((equipmentOptions) =>
        this.filterTemplateEquipmentOptions(
          equipmentOptions,
          query,
          currentValues
        )
      )
    );
  }

  getEquipmentOptionById(equipmentId: number): IInventoryEquipmentOption {
    // Handle options in case this.templateEquipmentOptions are not loaded yet
    const equipmentOptionMatch = this.templateEquipmentOptions?.find(
      (equipmentOption) => equipmentOption.id === equipmentId
    );
    const equipmentOptionToReturn = equipmentOptionMatch
      ? { ...equipmentOptionMatch, equipmentKeyId: equipmentOptionMatch.id }
      : null;

    return equipmentOptionToReturn;
  }

  addTemplateEquipments(
    templateTypeId: string,
    equipments: IInventoryEquipmentTypeOption[]
  ) {
    return this.httpToolTracker
      .post(TEMPLATE_EQUIPMENTS_ENDPOINT(templateTypeId), equipments)
      .pipe(this.successNotification);
  }

  updateTemplateDetails(templateId: string, detail: IInventoryTemplateDetail) {
    const url = TEMPLATE_DETAIL_ENDPOINT(templateId);

    return this.httpToolTracker
      .post(url, detail)
      .pipe(this.successNotification);
  }

  deleteTemplateDetail(templateId: string, detail: IInventoryTemplateDetail) {
    const url = REMOVE_TEMPLATE_DETAIL(
      templateId,
      detail.inventoryTemplateDetailId
    );
    return this.httpToolTracker.delete(url).pipe(this.successNotification);
  }

  getItems(): Observable<ItemDTO[]> {
    return this.httpToolTracker.get<ItemDTO[]>(GET_ITEMS).pipe(map(parseData));
  }

  getItemsList(): Observable<ItemListDTO[]> {
    return this.httpToolTracker
      .get<ItemListDTO[]>(GET_ITEMS_LIST)
      .pipe(map(parseData));
  }

  getItemGroups(): Observable<IInventoryTemplateItemGroup[]> {
    return this.httpToolTracker
      .get<IInventoryTemplateItemGroup[]>(GET_ITEM_GROUPS)
      .pipe(
        tap((itemGroups) => (this.itemGroups = itemGroups)),
        shareReplay(1)
      );
  }

  patchItemGroupInstructions(
    itemGroupId: string,
    groupInstructions: string
  ): Observable<IInventoryTemplateItemGroup> {
    const url = PATCH_ITEM_GROUP_ENDPOINT(itemGroupId);
    return this.httpToolTracker
      .patch(url, [
        {
          op: 'replace',
          path: '/itemGroupInstructions',
          value: groupInstructions,
        },
      ])
      .pipe(
        tap<IInventoryTemplateItemGroup>(() =>
          this.win.showToast$.next('Tool group instructions updated')
        )
      );
  }

  patchItemInstructions(
    itemId: string,
    itemInstructions: string
  ): Observable<IInventoryTemplateItemGroup> {
    const url = PATCH_ITEM_ENDPOINT(itemId);
    return this.httpToolTracker
      .patch(url, [
        {
          op: 'replace',
          path: '/itemInstructions',
          value: itemInstructions,
        },
      ])
      .pipe(
        tap<IInventoryTemplateItemGroup>(() =>
          this.win.showToast$.next('Tool instructions updated')
        )
      );
  }

  getInventoryCountStatus(): Observable<InventoryCountStatusDto[]> {
    return this.httpToolTracker
      .get<InventoryCountStatusDto[]>('/api/inventorycountstatus')
      .pipe(map(parseData));
  }

  getTetherings(): Observable<ItemDTO[]> {
    return this.httpToolTracker
      .get<ItemDTO[]>(GET_TETHERINGS)
      .pipe(map(parseData));
  }

  getManufacturers(): Observable<Manufacturer[]> {
    return this.httpToolTracker
      .get<Manufacturer[]>(GET_MANUFACTURERS)
      .pipe(map(parseData));
  }

  getVendors(): Observable<VendorDto[]> {
    return this.httpToolTracker
      .get<VendorDto[]>(GET_VENDORS)
      .pipe(map(parseData));
  }

  getUnitOfMeasures(): Observable<UnitOfMeasureDTO[]> {
    return this.httpToolTracker
      .get<UnitOfMeasureDTO[]>(GET_UNITOFMEASURES)
      .pipe(map(parseData));
  }

  getMissingGroupDetails(
    templateId: string,
    detailsGroupId: string
  ): Observable<IInventoryTemplateDetail[]> {
    return this.httpToolTracker.get<IInventoryTemplateDetail[]>(
      MISSING_GROUPS_DETAILS(templateId, detailsGroupId)
    );
  }

  getGridDetailGroups(
    details: IInventoryTemplateDetail[]
  ): IGridGroup<IInventoryTemplateDetail>[] {
    return buildGridDetailGroups(details, this.itemGroups);
  }

  /**
   * Returns an Observable that emits once the full (unfiltered) equipment
   * options list is in memory. Uses the same shareReplay(1) cache as
   * getEquipmentOptions(), so subsequent calls are free.  Callers that need
   * getEquipmentOptionById() to work synchronously should await this first.
   */
  ensureEquipmentOptionsLoaded(): Observable<IInventoryEquipmentOption[]> {
    if (!this.templateEquipmentOptions$) {
      this.getEquipmentOptions();
    }
    return this.templateEquipmentOptions$;
  }

  copyTemplate(
    sourceTemplateId: string,
    newTemplateName: string
  ): Observable<IInventoryTemplate> {
    const url = COPY_TEMPLATE(sourceTemplateId);
    return this.httpToolTracker
      .post<IInventoryTemplate>(url, { newTemplateName })
      .pipe(map(parseData));
  }

  deleteTemplate(templateId: string): Observable<IInventoryTemplate> {
    const url = TEMPLATE_ENDPOINT(templateId);
    return this.httpToolTracker
      .delete<IInventoryTemplate>(url)
      .pipe(map(parseData));
  }

  createTemplate(newTemplateName: string): Observable<IInventoryTemplate> {
    const url = CREATE_TEMPLATE;
    return this.httpToolTracker
      .post<IInventoryTemplate>(url, { newTemplateName })
      .pipe(map(parseData));
  }

  createTemplateDetail(itemId: string): Observable<IInventoryTemplateDetail> {
    const url = CREATE_TEMPLATE_DETAIL(itemId);
    return this.httpToolTracker
      .post<IInventoryTemplateDetail>(url, null)
      .pipe(map(parseData));
  }

  updateTemplateDetailSortOrder(inventoryTemplateDetailId: string) {
    return this.httpToolTracker
      .put(
        `/api/InventoryTemplate/itemGroupSortOrder/${inventoryTemplateDetailId}`,
        null
      )
      .pipe(map(parseData));
  }

  reorderTemplateItems(
    itemDetails: IInventoryTemplateDetail[]
  ): Observable<IInventoryTemplateDetail[]> {
    return this.httpToolTracker
      .put<
        IInventoryTemplateDetail[]
      >(BULK_EDIT_TEMPLATE_SORT_ORDER, itemDetails)
      .pipe(map(parseData));
  }

  reorderTemplateGroups(
    inventoryTemplateId: string,
    sortArray
  ): Observable<void> {
    const url = SORT_TEMPLATE_GROUPS(inventoryTemplateId);
    return this.httpToolTracker.put<void>(url, sortArray);
  }

  reorderCountItems(
    itemDetails: IInventoryCountDetail[]
  ): Observable<IInventoryCountDetail[]> {
    return this.httpToolTracker
      .put<IInventoryCountDetail[]>(BULK_EDIT_COUNT_SORT_ORDER, itemDetails)
      .pipe(map(parseData));
  }

  reorderCountGroups(inventoryCountId: string, sortArray): Observable<void> {
    return this.httpToolTracker.put<void>(
      SORT_COUNT_GROUPS(inventoryCountId),
      sortArray
    );
  }

  private filterTemplateEquipmentOptions(
    templateEquipmentOptions = this.templateEquipmentOptions,
    query?: string,
    currentValues?: IInventoryEquipmentOption[]
  ): IInventoryEquipmentOption[] {
    const currentValuesIds =
      currentValues?.map((currentValue) => currentValue.id) || [];

    return templateEquipmentOptions
      .filter(
        (equipmentOption) =>
          !currentValuesIds.includes(equipmentOption.id) &&
          (equipmentOption.name
            ?.toLowerCase()
            ?.includes(query?.toLowerCase()) ||
            equipmentOption.description
              ?.toLowerCase()
              ?.includes(query?.toLowerCase()))
      )
      .slice(0, 50);
  }

  private successNotification = (observable: Observable<never>) =>
    observable.pipe(tap(() => this.win.showToast$.next('Template updated')));
}
